import { createTag, deleteTag, getAllTags, updateChannelTags, updateTag } from 'api/socket.request'
import { AxiosResponse } from 'axios'
import { useConfirmV2 } from 'contexts/ConfirmV2Provider'
import { useToast } from 'contexts/ToastProvider'
import {
    ChannelTagUpdateData,
    CreateTagData,
    ITag,
    TagColorHex,
    UpdateTagData,
} from 'interfaces/tag.interface'
import React, { createContext, useContext, useRef } from 'react'
import {
    QueryObserverResult,
    RefetchOptions,
    RefetchQueryFilters,
    useMutation,
    UseMutationResult,
    useQuery,
} from 'react-query'
import { useRecoilValue } from 'recoil'
import { userState } from 'store/user'

interface TagContextType {
    handleCreateTag: (
        name: string,
        description: string,
        color: TagColorHex,
        handleClose: () => void
    ) => void
    handleUpdateTag: (
        name: string,
        description: string,
        color: TagColorHex,
        id: string,
        chatRoomCount: number,
        prevDescription: string | null,
        handleClose: () => void
    ) => void
    handleDeleteTag: (tagId: string, chatRoomCount: number) => void
    handleChannelTagUpdate: ({
        data,
        handleSuccess,
        handleDeletedTagError,
    }: {
        data: ChannelTagUpdateData
        handleSuccess: (tags: ITag[]) => void
        handleDeletedTagError?: (validTags: ITag[], allTagIds: string[]) => void
    }) => void
    allTags: ITag[] | undefined
    isAllTagsLoading: boolean
    createTagMutation: UseMutationResult<AxiosResponse<any, any>, unknown, CreateTagData, unknown>
    updateTagMutation: UseMutationResult<AxiosResponse<any, any>, unknown, UpdateTagData, unknown>
    updateChannelTagsMutation: UseMutationResult<
        AxiosResponse<any, any>,
        unknown,
        ChannelTagUpdateData,
        unknown
    >
    allTagsRefetch: <TPageData>(
        options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
    ) => Promise<QueryObserverResult<ITag[] | undefined, unknown>>
    contentLimit: Record<string, number>
}

const TagContext = createContext<TagContextType | undefined>(undefined)

const CONTENT_LIMIT = {
    name: 20,
    description: 100,
}

const MultipleTagProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { toast } = useToast()
    const { confirm } = useConfirmV2()
    const user = useRecoilValue(userState)
    const loading = useRef({
        createTag: false,
        updateTag: false,
        deleteTag: false,
    })

    const {
        data: allTags,
        isLoading: isAllTagsLoading,
        refetch: allTagsRefetch,
    } = useQuery(
        ['tags', user?.userId],
        async () => {
            const response = await getAllTags()
            return response.data.data
        },
        { enabled: !!user }
    )

    const createTagMutation = useMutation((tagData: CreateTagData) => createTag(tagData))

    const updateTagMutation = useMutation((tagData: UpdateTagData) =>
        updateTag(tagData.id, tagData)
    )

    const deleteTagMutation = useMutation((id: string) => deleteTag(id))

    const updateChannelTagsMutation = useMutation((data: ChannelTagUpdateData) =>
        updateChannelTags(data)
    )

    // 태그 에러 핸들러
    const handleTagError = (err: any, handleClose: () => void = () => {}) => {
        if (err.code && err.code === '500010') {
            // 태그가 삭제된 경우
            confirm({
                title: '',
                description: (
                    <p className="font-medium">
                        이 태그는 삭제된 상태입니다. <br />
                        새로운 태그를 만들어주세요.
                    </p>
                ),
                invisibleCancel: true,
                autoFocus: true,
                onConfirm: () => {
                    // 이미 삭제된 태그일 경우 모달 닫고 데이터 리패치
                    allTagsRefetch()
                    handleClose()
                },
            })
        } else if (err.code && err.code === '500004') {
            // 중복된 태그명
            confirm({
                title: '이미 등록된 태그 이름입니다.',
                description: (
                    <p>
                        태그는 중복으로 만들 수 없습니다. <br />
                        다른 이름으로 만들어주세요.
                    </p>
                ),
                invisibleCancel: true,
                autoFocus: true,
            })
        } else if (err.code && err.code === '500005') {
            // 태그 생성 개수 초과
            confirm({
                title: '태그는 최대 100개까지 만들 수 있습니다.',
                description: (
                    <p>
                        새로운 태그를 만드려면 <br />
                        사용하지 않는 태그를 먼저 정리해주세요.
                    </p>
                ),
                invisibleCancel: true,
                autoFocus: true,
            })
        } else {
            // 예상치 못한 에러
            confirm({
                title: '일시적인 문제가 발생했습니다.',
                description: (
                    <p>
                        잠시 후 다시 시도해주세요. <br />
                        문제가 반복될 경우 고객센터로 문의해주세요.
                    </p>
                ),
                invisibleCancel: true,
                autoFocus: true,
            })
        }
    }

    // 태그 생성
    const handleCreateTag = (
        name: string,
        description: string,
        color: TagColorHex,
        handleClose: () => void
    ) => {
        // 중복 호출 제어
        if (loading.current.createTag) return
        loading.current.createTag = true

        const tagName = name.replace(/\s+/g, '')
        const tagDescription = description.trim()
        const tagData: CreateTagData = {
            name: tagName,
            color,
        }

        if (tagDescription) {
            tagData.description = tagDescription
        }

        createTagMutation.mutate(tagData, {
            onSuccess: () => {
                allTagsRefetch()
                toast({ message: '태그가 생성되었습니다.', status: 'complete' })
                handleClose()
            },
            onError: (err) => handleTagError(err),
            onSettled: () => {
                loading.current.createTag = false
            },
        })
    }

    // 태그 수정
    const handleUpdateTag = (
        name: string,
        description: string,
        color: TagColorHex,
        id: string,
        chatRoomCount: number,
        prevDescription: string | null,
        handleClose: () => void
    ) => {
        // 중복 호출 제어
        if (loading.current.updateTag) return
        loading.current.updateTag = true
        confirm({
            title: '태그 정보를 수정하시겠습니까?',
            description: (
                <p>
                    수정 사항은 이 태그가 적용된 <br />
                    <span className="text-accect">{chatRoomCount}개의 문의톡에 모두 적용</span>
                    됩니다.
                </p>
            ),
            onConfirm: () => {
                const tagName = name.replace(/\s+/g, '')
                const tagDescription = description.trim()
                const tagData: UpdateTagData = {
                    name: tagName,
                    color,
                    id,
                }

                // 설명글이 있다면 설명글을 업데이트하기 위하여 포함
                if (tagDescription) {
                    tagData.description = tagDescription
                }

                // 이전 설명글이 있고 현재 설명글이 없다면 설명글을 삭제하기 위하여 포함
                if (prevDescription && tagDescription === '') {
                    tagData.description = null
                }

                updateTagMutation.mutate(tagData, {
                    onSuccess: (res) => {
                        if (res.status === 404) {
                            // 업데이트 시 이미 삭제되었다면 에러 처리
                            throw res
                        }

                        allTagsRefetch()
                        handleClose()
                        toast({ message: '태그가 수정되었습니다.', status: 'complete' })
                    },
                    onError: (err: any) => handleTagError(err, handleClose),
                    onSettled: () => {
                        loading.current.updateTag = false
                    },
                })
            },
            onCancel: () => {
                loading.current.updateTag = false
            },
        })
    }

    // 태그 삭제
    const handleDeleteTag = (tagId: string, chatRoomCount: number) => {
        // 중복 호출 제어
        if (loading.current.deleteTag) return
        loading.current.deleteTag = true
        confirm({
            title: '태그를 삭제하시겠습니까?',
            description: (
                <p>
                    이 태그가 적용된
                    <br />
                    <span className="text-accect">{chatRoomCount}개의 문의톡에서 모두 삭제</span>
                    됩니다.
                </p>
            ),
            onConfirm: () => {
                deleteTagMutation.mutate(tagId, {
                    onSuccess: (res) => {
                        if (res.status === 404) {
                            // 삭제 시 이미 삭제되었다면 에러 처리
                            throw res
                        }
                        allTagsRefetch()
                        toast({ message: '태그가 삭제되었습니다.', status: 'complete' })
                    },
                    onError: (err: any) => {
                        if (err.code && err.code === '500010') {
                            // 태그 삭제를 시도할 때 이미 삭제된 경우 에러 핸들러로 넘기지 않고 별도의 모달 표시
                            confirm({
                                title: '',
                                description: <p className="font-medium">이미 삭제된 태그입니다.</p>,
                                onConfirm: () => {
                                    allTagsRefetch()
                                },
                                invisibleCancel: true,
                            })
                        } else {
                            handleTagError(err)
                        }
                    },
                    onSettled: () => {
                        loading.current.deleteTag = false
                    },
                })
            },
            onCancel: () => {
                loading.current.deleteTag = false
            },
        })
    }

    // 채널 태그 업데이트
    const handleChannelTagUpdate = ({
        data,
        handleSuccess,
        handleDeletedTagError,
    }: {
        data: ChannelTagUpdateData
        handleSuccess: (tags: ITag[]) => void
        handleDeletedTagError?: (validTags: ITag[], allTagIds: string[]) => void
    }) => {
        updateChannelTagsMutation.mutate(data, {
            onSuccess: (res) => {
                const tagList = res.data.data?.map((tag) => tag.tag) || []
                handleSuccess(tagList)
            },
            onError: async (err: any) => {
                // 알 수 없는 에러 모달
                const showErrorModal = (error: any) => {
                    confirm({
                        title: '일시적인 문제가 발생했습니다.',
                        description: (
                            <p>
                                잠시 후 다시 시도해주세요.
                                <br />
                                문제가 반복될 경우 고객센터로 문의해주세요.
                                <br />
                                {error?.code || ''}
                            </p>
                        ),
                        invisibleCancel: true,
                    })
                }

                // 채널 태그 업데이트 에러 핸들러
                if ('code' in err && err.code === '500007') {
                    try {
                        // 채널에 할당하려는 태그가 삭제된 경우 실행
                        // 요청에 사용된 태그 복사
                        const prevSelectedTags = structuredClone(data.tags)
                        // 현재 사용 가능한 모든 태그 조회
                        const newValidAllTagIds = await allTagsRefetch().then(
                            (res) => res.data?.map((tag) => tag.id) || []
                        )

                        const validTags: ITag[] = []
                        const invalidTags: ITag[] = []

                        prevSelectedTags.forEach((tag) => {
                            if (newValidAllTagIds?.includes(tag.id)) {
                                validTags.push(tag)
                            } else {
                                invalidTags.push(tag)
                            }
                        })

                        // 삭제된 태그 처리 함수 실행
                        if (handleDeletedTagError) {
                            handleDeletedTagError(validTags, newValidAllTagIds)
                        }

                        const invalidTagNames = invalidTags.map((tag) => tag.name)

                        confirm({
                            title: '삭제된 태그가 포함되어 있습니다.',
                            description: (
                                <div className="max-w-[400px] text-secondary-100">
                                    <p>
                                        [확인] 을 누르면 삭제된 태그는 목록에서 자동으로 제거됩니다.
                                        <br />
                                        적용할 태그를 확인한 후, 다시 시도해주세요.
                                        <br />
                                        <br />
                                        <span>▶ 삭제된 태그 : {invalidTagNames.join(', ')}</span>
                                    </p>
                                </div>
                            ),
                            invisibleCancel: true,
                        })
                    } catch (error) {
                        // 삭제된 태그를제거하고 최신화 하는 과정에서 에러 발생한 경우 알 수 없는 에러 모달 표시
                        showErrorModal(error)
                    }
                } else {
                    // 태그가 삭제된 경우 이외의 모든 에러 처리
                    showErrorModal(err)
                }
            },
        })
    }

    return (
        <TagContext.Provider
            value={{
                handleCreateTag,
                handleUpdateTag,
                handleDeleteTag,
                handleChannelTagUpdate,
                allTags,
                isAllTagsLoading,
                createTagMutation,
                updateTagMutation,
                updateChannelTagsMutation,
                allTagsRefetch,
                contentLimit: CONTENT_LIMIT,
            }}
        >
            {children}
        </TagContext.Provider>
    )
}

export default MultipleTagProvider

export const useTags = (): TagContextType => {
    const context = useContext(TagContext)
    if (!context) {
        throw new Error('useTags must be used within a MultipleTagProvider')
    }
    return context
}
